(() => {
  'use strict';

  const escapeHtml = value => String(value ?? '').replace(/[&<>"']/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character]);
  const readConfig = root => {
    try { return JSON.parse(root.querySelector('script[type="application/json"]')?.textContent || '{}'); }
    catch (error) { console.error('Wellmony configuration is invalid.', error); return {}; }
  };
  const emit = (name, detail = {}) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: name, wm_staging: Boolean(window.WellmonyTheme?.isStaging), ...detail });
    document.dispatchEvent(new CustomEvent(`wellmony:${name}`, { detail }));
  };
  const showToast = message => {
    const toast = document.querySelector('[data-wm-toast]');
    if (!toast) return;
    toast.textContent = message;
    toast.hidden = false;
    clearTimeout(window.wmToastTimer);
    window.wmToastTimer = setTimeout(() => { toast.hidden = true; }, 3200);
  };

  const menu = document.querySelector('[data-wm-menu]');
  const navigation = document.querySelector('[data-wm-navigation]');
  menu?.addEventListener('click', () => {
    const open = menu.getAttribute('aria-expanded') === 'true';
    menu.setAttribute('aria-expanded', String(!open));
    navigation?.classList.toggle('is-open', !open);
  });
  navigation?.querySelectorAll('a').forEach(link => link.addEventListener('click', () => {
    menu?.setAttribute('aria-expanded', 'false');
    navigation?.classList.remove('is-open');
  }));

  class WellmonyExperience extends HTMLElement {
    connectedCallback() {
      this.config = readConfig(this);
      this.querySelector('[data-wm-static-title]')?.remove();
      this.context = { schemaVersion: this.config.schemaVersion || 'wm2-foundation-v1', stateKey: '', momentKey: '', durationKey: '', lifeStageKey: this.dataset.lifeStage || 'life-stage.general', sensoryPreferenceKeys: [] };
      this.step = 0;
      const query = new URLSearchParams(location.search);
      if (this.config.states?.some(item => item.key === query.get('state'))) this.context.stateKey = query.get('state');
      if (this.config.moments?.some(item => item.key === query.get('moment'))) this.context.momentKey = query.get('moment');
      if (this.config.durations?.some(item => item.key === query.get('duration'))) this.context.durationKey = query.get('duration');
      this.steps = [
        { key: 'stateKey', event: 'state_selected', question: this.config.labels?.state || 'How do you feel?', items: this.config.states || [] },
        { key: 'momentKey', event: 'moment_selected', question: this.config.labels?.moment || 'What moment are you in?', items: this.config.moments || [] },
        { key: 'durationKey', event: 'duration_selected', question: this.config.labels?.duration || 'How much time do you have?', items: this.config.durations || [] },
        { key: 'sensoryPreferenceKeys', event: 'sensory_preference_selected', question: this.config.labels?.sense || 'What sounds good right now?', items: this.config.senses || [], multi: true }
      ];
      while (this.step < 3 && this.context[this.steps[this.step].key]) this.step += 1;
      emit('reset_started', { source: this.dataset.source || 'reset-page' });
      this.render();
    }

    label(collection, key) { return collection?.find(item => item.key === key)?.label || key; }
    values(value) { return Array.isArray(value) ? value : String(value || '').split(',').map(item => item.trim()).filter(Boolean); }
    matches(rule, field, value) { const values = this.values(rule[field]); return !values.length || values.includes('*') || values.includes(value); }
    recommendation() {
      const ranked = (this.config.recommendations || []).filter(rule =>
        this.matches(rule, 'states', this.context.stateKey) && this.matches(rule, 'moments', this.context.momentKey) && this.matches(rule, 'durations', this.context.durationKey) && this.matches(rule, 'lifeStages', this.context.lifeStageKey)
      ).map(rule => { const senses = this.values(rule.senses); return { ...rule, score: Number(rule.priority || 0) + this.context.sensoryPreferenceKeys.filter(key => senses.includes('*') || senses.includes(key)).length * 20 }; });
      return ranked.sort((a, b) => b.score - a.score)[0] || this.config.fallback;
    }

    render() {
      if (this.step >= this.steps.length) return this.renderResult();
      const step = this.steps[this.step];
      const choices = step.items.map((item, index) => {
        const selected = step.multi ? this.context.sensoryPreferenceKeys.includes(item.key) : this.context[step.key] === item.key;
        return `<button class="wm-option" type="button" data-choice="${escapeHtml(item.key)}" role="${step.multi ? 'checkbox' : 'radio'}" aria-checked="${selected}"><small>${String(index + 1).padStart(2, '0')}</small><span>${escapeHtml(item.label)}</span><span aria-hidden="true">${selected ? '●' : '○'}</span></button>`;
      }).join('');
      this.querySelector('[data-wm-experience-view]').innerHTML = `<div class="wm-progress-meta"><span>Reset Finder</span><span>${this.step + 1} / ${this.steps.length}</span></div><div class="wm-progress" aria-hidden="true"><i style="width:${((this.step + 1) / this.steps.length) * 100}%"></i></div><p class="wm-eyebrow">Start where you are</p><h1 class="wm-question" tabindex="-1">${escapeHtml(step.question)}</h1><div class="wm-options" role="${step.multi ? 'group' : 'radiogroup'}" aria-label="${escapeHtml(step.question)}">${choices}</div><div class="wm-flow-actions">${this.step ? '<button class="wm-back" type="button" data-back>← Back</button>' : '<span></span>'}${step.multi ? '<button class="wm-button" type="button" data-show-result>Show my reset <span>→</span></button>' : ''}</div>`;
      if (this.hasRendered) requestAnimationFrame(() => this.querySelector('.wm-question')?.focus());
      this.hasRendered = true;
      this.bindStep(step);
    }

    bindStep(step) {
      this.querySelectorAll('[data-choice]').forEach(button => button.addEventListener('click', () => {
        const value = button.dataset.choice;
        if (step.multi) {
          this.context.sensoryPreferenceKeys = this.context.sensoryPreferenceKeys.includes(value) ? this.context.sensoryPreferenceKeys.filter(item => item !== value) : [...this.context.sensoryPreferenceKeys, value];
          emit(step.event, { selection_key: value, selected: this.context.sensoryPreferenceKeys.includes(value) });
          this.render();
        } else {
          this.context[step.key] = value;
          emit(step.event, { selection_key: value });
          this.step += 1;
          while (this.step < 3 && this.context[this.steps[this.step].key]) this.step += 1;
          this.render();
        }
      }));
      this.querySelector('[data-back]')?.addEventListener('click', () => { this.step = Math.max(0, this.step - 1); this.render(); });
      this.querySelector('[data-show-result]')?.addEventListener('click', () => { this.step = this.steps.length; this.renderResult(); });
    }

    renderResult() {
      const result = this.recommendation();
      if (!result) {
        this.querySelector('[data-wm-experience-view]').innerHTML = '<div class="wm-empty"><h1 class="wm-section-title">Your reset is almost ready.</h1><p>Please start again or choose another combination.</p><button class="wm-back" data-restart>Start again</button></div>';
        this.querySelector('[data-restart]').addEventListener('click', () => { this.step = 0; this.render(); });
        return;
      }
      emit('reset_recommended', { recommendation_key: result.key, catalog_version: this.config.catalogVersion });
      const duration = this.config.durations?.find(item => item.key === this.context.durationKey);
      const senses = this.context.sensoryPreferenceKeys.map(key => this.label(this.config.senses, key));
      this.querySelector('[data-wm-experience-view]').innerHTML = `<div class="wm-result"><article class="wm-result-main" aria-live="polite"><div class="wm-result-duration">${escapeHtml(duration?.minutes || '')} <span>MIN</span></div><p class="wm-eyebrow">Your reset · ${escapeHtml(this.label(this.config.moments, this.context.momentKey))}</p><h1 class="wm-section-title" tabindex="-1">${escapeHtml(result.title)}</h1><div class="wm-chips"><span class="wm-chip">${escapeHtml(this.label(this.config.states, this.context.stateKey))}</span>${senses.map(label => `<span class="wm-chip">${escapeHtml(label)}</span>`).join('')}</div><p class="wm-result-instruction">${escapeHtml(result.instruction)}</p><button class="wm-button wm-button--light" type="button" data-start-reset>Begin this reset <span>→</span></button></article><aside class="wm-result-next"><p class="wm-eyebrow">Only if you want to continue</p><h2>${escapeHtml(result.nextHeading || 'Want to make this a ritual?')}</h2><p>${escapeHtml(result.nextCopy || '')}</p>${result.nextUrl ? `<a class="wm-line-link" href="${escapeHtml(result.nextUrl)}">${escapeHtml(result.nextLabel || 'Explore the next step')} <span>↗</span></a>` : ''}</aside><button class="wm-back" type="button" data-restart>Start again</button></div>`;
      requestAnimationFrame(() => this.querySelector('.wm-result-main h1')?.focus());
      this.querySelector('[data-start-reset]')?.addEventListener('click', () => { emit('reset_completed', { reset_key: result.resetKey || result.key }); showToast('Your reset has begun. Leave this screen when you are ready.'); });
      this.querySelector('[data-restart]')?.addEventListener('click', () => { this.step = 0; this.context = { ...this.context, stateKey: '', momentKey: '', durationKey: '', sensoryPreferenceKeys: [] }; this.render(); });
    }
  }

  class WellmonyOffline extends HTMLElement {
    connectedCallback() {
      this.config = readConfig(this);
      this.querySelectorAll('[data-offline-duration]').forEach(button => button.addEventListener('click', () => this.start(button.dataset.offlineDuration)));
      const requestedDuration = new URLSearchParams(location.search).get('duration');
      if (requestedDuration && this.config.resets?.some(item => item.durationKey === requestedDuration)) {
        requestAnimationFrame(() => this.start(requestedDuration));
      }
    }
    start(key) {
      const reset = this.config.resets?.find(item => item.durationKey === key);
      if (!reset) return;
      this.returnFocus = document.activeElement;
      emit('go_offline_started', { duration_key: key });
      const quiet = this.querySelector('[data-quiet]');
      quiet.hidden = false;
      document.body.classList.add('wm-quiet-active');
      quiet.querySelector('[data-quiet-time]').textContent = `${reset.minutes} MIN`;
      quiet.querySelector('[data-quiet-title]').textContent = reset.title;
      quiet.querySelector('[data-quiet-instruction]').textContent = reset.instruction;
      quiet.querySelector('[data-return]').hidden = false;
      quiet.querySelector('[data-feedback]').hidden = true;
      quiet.querySelector('[data-complete]').hidden = true;
      quiet.querySelector('[data-return]').onclick = () => this.return(reset);
      quiet.querySelector('[data-close-quiet]').onclick = () => this.close();
      quiet.querySelector('[data-close-complete]').onclick = () => this.close();
      quiet.onkeydown = event => { if (event.key === 'Escape') this.close(); };
      requestAnimationFrame(() => quiet.querySelector('[data-quiet-title]')?.focus());
    }
    return(reset) {
      emit('go_offline_completed', { duration_key: reset.durationKey });
      const quiet = this.querySelector('[data-quiet]');
      quiet.querySelector('[data-quiet-title]').textContent = this.config.returnHeading || 'Welcome back.';
      quiet.querySelector('[data-quiet-instruction]').textContent = this.config.feedbackPrompt || 'How do you feel?';
      quiet.querySelector('[data-return]').hidden = true;
      quiet.querySelector('[data-feedback]').hidden = false;
      requestAnimationFrame(() => quiet.querySelector('[data-feedback-value]')?.focus());
      quiet.querySelectorAll('[data-feedback-value]').forEach(button => button.onclick = () => {
        emit('reset_feedback_submitted', { feedback: button.dataset.feedbackValue, duration_key: reset.durationKey });
        quiet.querySelector('[data-feedback]').hidden = true;
        quiet.querySelector('[data-quiet-title]').textContent = this.config.completeHeading || 'Your reset is complete.';
        quiet.querySelector('[data-quiet-instruction]').textContent = this.config.thankYou || 'Notice what changed—without judging it.';
        quiet.querySelector('[data-complete]').hidden = false;
        requestAnimationFrame(() => quiet.querySelector('[data-complete] a')?.focus());
      });
    }
    close() { this.querySelector('[data-quiet]').hidden = true; document.body.classList.remove('wm-quiet-active'); this.returnFocus?.focus?.(); }
  }

  customElements.define('wm-experience', WellmonyExperience);
  customElements.define('wm-offline', WellmonyOffline);
})();
